import { getEngineApiClient } from '../../api-client/axios-client.js'
import { addDays } from '../../utils/date.js'

const state = {
  days: [],
  restaurantTimes: ['18:30', '19:00', '19:30', '20:00', '20:30'],
  calculatedPricing: false,
  pricing: {}
}

const getters = {
  getFirstHotel: (state, getters) => {
    if (state.days[0].hotelId === 0) {
      return null
    }

    return getters.getCountyHotels.find(htl => {
      return htl.productId === state.days[0].hotelId
    })
  },
  getLastHotel: (state, getters) => {
    // the last hotel is on the penultimate day (hence -2 here)
    const index = state.days.length - 2
    if (state.days[index].hotelId === 0) {
      return null
    }

    return getters.getCountyHotels.find(htl => {
      return htl.productId === state.days[index].hotelId
    })
  },
  getSelectedHotels: (state, getters) => {
    let hotelIds = [
      ...new Set(
        state.days.map(day => {
          return day.hotelId
        })
      )
    ]
    hotelIds = hotelIds.filter(id => {
      return id > 0
    })

    return getters.getCountyHotels.filter(htl => {
      return (
        hotelIds.findIndex(productId => {
          return htl.productId === productId
        }) > -1
      )
    })
  },
  getSelectedExcursions: (state, getters) => {
    let excursionIds = [
      ...new Set(
        state.days.map(day => {
          return day.excursionId
        })
      )
    ]
    excursionIds = excursionIds.filter(id => {
      return id > 0
    })

    return getters.getCountyExcursions.filter(excr => {
      return (
        excursionIds.findIndex(productId => {
          return excr.productId === productId
        }) > -1
      )
    })
  },
  getSelectedEntertainments: (state, getters) => {
    let entertainmentIds = [
      ...new Set(
        state.days.map(day => {
          return day.entertainmentId
        })
      )
    ]
    entertainmentIds = entertainmentIds.filter(id => {
      return id > 0
    })

    return getters.getCountyEntertainments.filter(ent => {
      return (
        entertainmentIds.findIndex(productId => {
          return ent.productId === productId
        }) > -1
      )
    })
  },
  getPricingRequestPostData: (state, getters, rootState) => {
    return {
      fromDate: rootState.start.fromDate,
      currencyId: getters.getEuroCurrency.currencyId,
      transferInProductId: getters.getTransferInProductId,
      transferOutProductId: getters.getTransferOutProductId,
      numberOfAdults: rootState.passengers.noOfAdults,
      numberOfChildren: rootState.passengers.noOfChildren,
      numberOfInfants: rootState.passengers.noOfInfants,
      days: state.days.map(day => {
        return {
          dayNumber: day.number,
          excursionId: day.excursionId,
          entertainmentId: day.entertainmentId,
          entertainmentTime: day.entertainmentTime,
          hotelId: day.hotelId
        }
      }),
      roomingInfos: rootState.rooming.rooms
    }
  }
}

const mutations = {
  initDbdDays: (state, payload) => {
    state.days = payload
  },
  setDialogProductType: (state, payload) => {
    state.dialogProductType = payload.productType
  },
  setHotel: (state, payload) => {
    const day = state.days.find(d => d.number === payload.dayNo)
    day.hotelId = payload.productId
  },
  setExcursion: (state, payload) => {
    const day = state.days.find(d => d.number === payload.dayNo)
    day.excursionId = payload.productId
  },
  setEntertainment: (state, payload) => {
    const day = state.days.find(d => d.number === payload.dayNo)
    day.entertainmentId = payload.productId
    day.entertainmentTime = payload.productTime
  },
  removeExcursion: (state, payload) => {
    const day = state.days.find(d => d.number === payload)
    day.excursionId = 0
  },
  removeEntertainment: (state, payload) => {
    const day = state.days.find(d => d.number === payload)
    day.entertainmentId = 0
  },
  setCalculatedPricing: (state, payload) => {
    state.calculatedPricing = payload
  },
  setPricing: (state, payload) => {
    state.pricing = payload
  }
}

const actions = {
  initDbdDays: ({ commit, dispatch, state, getters, rootState }) => {
    // do not re-initialise if the number of days has not changed
    if (state.days != null && state.days.length === getters.noOfDays) {
      return
    }

    // ok re-initialise
    const dateOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
    const days = []
    for (let i = 1; i <= getters.noOfDays; i++) {
      days.push({
        number: i,
        date: addDays(rootState.start.fromDate, i - 1).toLocaleString(
          undefined,
          dateOptions
        ),
        excursionId: 0,
        entertainmentId: 0,
        entertainmentTime: '00:00',
        hotelId: 0
      })
    }

    // as we have reset the days we need to reset any rooming information too
    dispatch('resetRooms')

    // finally commit the new array of days
    commit('initDbdDays', days)
  },
  setDialogProductType: ({ commit }, payload) => {
    commit('setDialogProductType', payload)
  },
  addProduct: ({ state, getters, commit, dispatch, rootState }, payload) => {
    const product = rootState.county.products.find(s => {
      return s.productId === payload.productId
    })

    if (product.productType.productTypeCode === 'HTL') {
      // set hotel for this day and following days that have the same hotel id
      const currentHotelId = state.days.find(d => {
        return d.number === payload.dayNo
      }).hotelId
      let lastDaySet = payload.dayNo

      if (currentHotelId !== payload.productId) {
        state.days
          .filter(d => {
            return d.number >= payload.dayNo
          })
          .forEach(d => {
            if (d.hotelId === currentHotelId && d.number < state.days.length) {
              commit('setHotel', {
                dayNo: d.number,
                productId: payload.productId
              })
              lastDaySet = d.number
            }
          })
      }

      // if we have set the first or last hotel, then check transfers
      if (payload.dayNo === 1 || lastDaySet === state.days.length) {
        dispatch('checkTransfers')
      }

      // if the current hotel id is no longer used then remove rooms associated with this hotel
      if (
        getters.getSelectedHotels.filter(htl => {
          return htl.productId === currentHotelId
        }).length === 0
      ) {
        dispatch('trimRooms', currentHotelId)
      }
    } else {
      if (
        product.productType.productTypeCode !== 'REST' &&
        product.fromTimeHours < 18
      ) {
        commit('setExcursion', payload)
      } else {
        commit('setEntertainment', payload)
      }
    }
  },
  removeProduct: ({ commit }, payload) => {
    if (payload.productType === 'Excursion') {
      commit('removeExcursion', payload.dayNo)
    } else if (payload.productType === 'Entertainment') {
      commit('removeEntertainment', payload.dayNo)
    }
  },
  async setPricing({ getters, dispatch, commit }) {
    commit('setCalculatedPricing', false)

    // get price information from api
    const client = getEngineApiClient()
    const data = getters.getPricingRequestPostData
    try {
      const response = await client.post('Booking/Price', data)

      // set the price and amount to pay
      commit('setPricing', response.data)

      // set that we have calculated the total price
      commit('setCalculatedPricing', true)
    } catch (error) {
      dispatch('setFatalError', error)
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
